import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Stethoscope, ArrowRight, ShieldCheck, GraduationCap, Building2, TrendingUp } from "lucide-react";
import api from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { CourseCard } from "@/components/CourseCard";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function Landing() {
  const nav = useNavigate();
  const [meta, setMeta] = useState({ cities: [], specialties: [] });
  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/courses").then((r) => setFeatured(r.data.slice(0, 6))).catch(() => {});
  }, []);

  const doSearch = () => {
    const p = new URLSearchParams();
    if (city) p.set("city", city);
    if (specialty) p.set("specialty", specialty);
    nav(`/courses?${p.toString()}`);
  };

  const roles = [
    { icon: Stethoscope, label: "Doctors", desc: "Fellowships & PG diplomas" },
    { icon: GraduationCap, label: "Nurses", desc: "Critical care & specialty nursing" },
    { icon: Building2, label: "Technicians", desc: "Imaging, dialysis & lab tech" },
    { icon: ShieldCheck, label: "Administrators", desc: "Hospital ops & quality" },
  ];

  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden hero-grid">
        <div className="pointer-events-none absolute -right-24 -top-24 h-96 w-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-semibold uppercase tracking-widest text-accent">
              <TrendingUp className="h-3.5 w-3.5" /> India's healthcare learning network
            </span>
            <h1 className="mt-6 font-heading text-4xl font-extrabold leading-none tracking-tight sm:text-5xl lg:text-6xl">
              Find your next clinical course.
              <span className="block text-primary">Advance where it matters.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg text-muted-foreground">
              Search fellowships, diplomas and certifications by city, specialty and interest — from verified
              teaching institutes across India. Apply and enroll directly.
            </p>
          </div>

          {/* Search widget */}
          <div className="mt-10 max-w-4xl rounded-2xl border border-border bg-card/90 p-4 shadow-lg backdrop-blur-xl">
            <div className="grid gap-3 md:grid-cols-[1fr_1fr_auto]">
              <div>
                <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><MapPin className="h-3.5 w-3.5" /> City / State</label>
                <Select value={city} onValueChange={setCity}>
                  <SelectTrigger data-testid="hero-city-select"><SelectValue placeholder="Any location" /></SelectTrigger>
                  <SelectContent>
                    {meta.cities.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="mb-1 flex items-center gap-1.5 text-xs font-semibold text-muted-foreground"><Stethoscope className="h-3.5 w-3.5" /> Specialty</label>
                <Select value={specialty} onValueChange={setSpecialty}>
                  <SelectTrigger data-testid="hero-specialty-select"><SelectValue placeholder="Any specialty" /></SelectTrigger>
                  <SelectContent>
                    {meta.specialties.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button data-testid="hero-search-btn" className="h-10 w-full md:w-auto" onClick={doSearch}>
                  <Search className="mr-2 h-4 w-4" /> Search
                </Button>
              </div>
            </div>
          </div>

          <div className="mt-6 flex flex-wrap gap-2 text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">Popular:</span>
            {["Emergency Medicine", "Cardiology", "Critical Care Nursing", "Radiology"].map((s) => (
              <button key={s} data-testid={`popular-${s.split(" ")[0].toLowerCase()}`}
                onClick={() => nav(`/courses?specialty=${encodeURIComponent(s)}`)}
                className="rounded-full border border-border bg-card px-3 py-1 transition-colors hover:border-primary hover:text-primary">
                {s}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Roles */}
      <section id="how" className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Built for every healthcare role</h2>
        <p className="mt-2 text-muted-foreground">From MBBS graduates to hospital administrators.</p>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {roles.map((r) => (
            <div key={r.label} data-testid={`role-${r.label.toLowerCase()}`}
              className="rounded-2xl border border-border bg-card p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-md">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-primary/10 text-primary">
                <r.icon className="h-6 w-6" />
              </span>
              <h3 className="mt-4 font-heading text-lg font-bold">{r.label}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{r.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="font-heading text-2xl font-bold tracking-tight sm:text-3xl">Featured programs</h2>
            <p className="mt-2 text-muted-foreground">Fresh courses with open admissions.</p>
          </div>
          <Button variant="ghost" data-testid="view-all-courses" onClick={() => nav("/courses")}>
            View all <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {featured.map((c) => <CourseCard key={c.id} course={c} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        <div className="grid gap-6 rounded-3xl border border-border bg-primary p-10 text-primary-foreground md:grid-cols-2 md:items-center">
          <div>
            <h2 className="font-heading text-2xl font-bold sm:text-3xl">Are you a teaching institute?</h2>
            <p className="mt-3 max-w-md text-primary-foreground/80">
              List your courses, manage applications and reach thousands of healthcare professionals across India.
            </p>
          </div>
          <div className="flex gap-3 md:justify-end">
            <Button variant="secondary" size="lg" data-testid="cta-institute-register" onClick={() => nav("/register")}>
              Register your institute <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
